/**
 * RemoteAbilityVFX — resolves the VFX *plays* that should fire on a remote
 * client when ANOTHER player casts an ability (Lane 5, VFX & Rendering Parity).
 *
 * THE GAP THIS CLOSES
 * -------------------
 * The server broadcasts every cast (`abilityCasted`, CombatPipeline.emitCast),
 * but the only remote consumers used to be:
 *   - EntityEventWiring.handleRemoteAbilityProjectile → projectiles only
 *   - VFXSystem.onRemoteAbilityCasted → above-head cast glow only
 * So AoE / ground / aura abilities (Blizzard, Meteor Shower, Inferno, Ring of
 * Frost, Flamestrike, Arcane Explosion — `target = aoe|ground`, no projectile
 * metadata) broadcast fine but drew NOTHING on remote screens.
 *
 * PARITY STRATEGY
 * ---------------
 * This module is the single source of truth for "what does an ability draw at
 * the caster". It mirrors the LOCAL caster path exactly:
 *   - AoE/ground (`TomlAbilityFactory` → `AOEBehavior`): a zone VFX
 *     `vfx.castEffect || 'aoe_blast'` at the caster's position, PLUS the
 *     ability's `impact_effect` at the same spot (school impact). The local
 *     `AOEBehavior` places its zone at `player.position` (optionally offset
 *     forward) — NOT at a separate ground target — so the broadcast payload's
 *     lack of ground coords is NOT a blocker for these categories.
 *   - self/buff (`withBuff`/`withHeal`): the cast aura `vfx.castEffect ||
 *     'defensive_aura'` tinted with `vfx.auraColor`, visible to others.
 * Resolution reuses `getAbilityVFX` (the same mapping the local path falls
 * through) so an unregistered raw effect id still lands on the right effect.
 *
 * The returned plays are fed to VFXSystem.spawnEffectFromData / playById, the
 * SAME players the local caster uses — identical effect ids → identical pixels.
 */

import { ABILITY_DATA } from '../../shared/content/generated/abilities.gen';
import { REMOTE_VFX_CONFIG } from './config/remote-vfx.config';
import { ABILITY_SCHOOL_COLORS as SCHOOL_COLORS } from '../../shared/constants/AbilitySchoolColors';
import { getAbilityVFX, getSchoolHitVFX } from './config/AbilityVFXMap';
import { resolveAbilityArchetype, archCastId, ENABLE_ARCHETYPE_CAST_FALLBACK } from '../../config/abilities/vfxArchetypes.config';
import { VFXEffectRegistry } from './VFXEffectRegistry';

/** One resolved VFX play: feed straight into VFXSystem.spawnEffectFromData. */
export interface RemoteVfxPlay {
  /** Raw effect id to play (resolved against the registry by the caller, with
   *  abilityId fallback through getAbilityVFX — same as the local path). */
  effectId: string;
  x: number;
  y: number;
  /** Overrides forwarded to the player (color/scale/duration/abilityId/...). */
  overrides: Record<string, unknown>;
  /** Category tag — for logging / tests, not rendering. */
  category: 'aoe_zone' | 'aoe_impact' | 'aura';
}


/**
 * Resolve the above-head cast GLOW another client should render for a remote
 * cast (U-vfx-authority-4; cast-side doctrine flip 2026-07-17). The
 * abilityCasted broadcast carries no school, so the old `broadcastSchool ||
 * 'physical'` fallback rendered ~277 unmapped abilities as a generic physical
 * glow remotely. Resolution order (cast-side-doctrine.md — the authored TOML
 * cast_effect outranks the curated map, mirroring the impact-side flip
 * e323f598 and the ProjectileBehavior wind-up chain, healing the local-vs-
 * remote cast parity split):
 *   TOML-authored cast_effect (registered at boot by the T3.1 generator,
 *   authored color included; SKIPPED when its registered def is a persistent
 *   `category:'status'` aura — buff realizations must not play as one-shot
 *   cast beats — or when opts.omitAuthoredCastEffect dedupes it)
 *   → curated map castVfxId → archetype cast fallback → school hit derived
 *   from the ability's own TOML.
 */
export function resolveRemoteCastGlowId(
  abilityId: string | undefined,
  broadcastSchool?: string,
  opts?: {
    /** Dedupe: another play already renders the authored cast_effect (local
     *  wind-up, or the remote zone/aura play — RemoteCastVFXHandler), so skip
     *  that tier to avoid playing the same effect twice. */
    omitAuthoredCastEffect?: boolean;
  },
): string {
  const mapping = abilityId ? getAbilityVFX(abilityId) : undefined;
  const toml = abilityId ? ABILITY_DATA[abilityId] : undefined;
  const school = String(broadcastSchool || toml?.school || 'physical');
  const archetype = toml ? resolveAbilityArchetype(toml.class, school) : undefined;
  // Status-category guard (doctrine §5 risk 3): buff cast effects realize as
  // persistent looping status auras (namedEffectGenerator persistent path);
  // playById-ing one as a fire-and-forget glow strands a looping visual. The
  // aura channel (resolveRemoteAbilityVfx / BuffBehavior) owns those defs.
  const authoredId = toml?.vfx?.castEffect;
  // The dedupe is by effect ID, not by tier (K27 remote half): when another
  // play already renders the authored cast_effect, a LOWER tier resolving to
  // the same id — wave-5 repointed curated map rows onto their authored ids
  // (bladestorm) — must not re-play it either. Ban the id from every tier.
  // The terminal school-hit fallback stays unbanned so the signature stays
  // `string`; callers that know their full play set (RemoteCastVFXHandler)
  // set-membership-skip the final id for residual overlaps.
  const banned = opts?.omitAuthoredCastEffect ? authoredId : undefined;
  const pick = (id: string | undefined): string | undefined =>
    id && id !== banned ? id : undefined;
  const authored = pick(authoredId);
  const authoredPlayable =
    authored && VFXEffectRegistry.get(authored)?.category !== 'status' ? authored : undefined;
  return authoredPlayable
    || pick(mapping?.castVfxId)
    || pick(ENABLE_ARCHETYPE_CAST_FALLBACK && archetype ? archCastId(archetype.cls, archetype.school) : undefined)
    || getSchoolHitVFX(school);
}

function parseColor(hex: string | undefined): number | undefined {
  if (!hex) return undefined;
  const n = parseInt(hex.replace('#', ''), 16);
  return Number.isNaN(n) ? undefined : n;
}

/**
 * Resolve the VFX plays a remote client should render for a cast made by
 * another player, given the caster's *current rendered position*.
 *
 * Returns [] when the ability is a plain projectile / single-target hit —
 * those are already covered by EntityEventWiring (projectile) and the
 * existing onRemoteAbilityCasted cast-glow path, so we must NOT double up.
 */
export function resolveRemoteAbilityVfx(
  abilityId: string,
  casterX: number,
  casterY: number,
  targetX?: number,
  targetY?: number,
): RemoteVfxPlay[] {
  const data = ABILITY_DATA[abilityId];
  if (!data) return [];

  const plays: RemoteVfxPlay[] = [];
  const vfx = data.vfx;
  const school = data.school || 'physical';
  const target = data.target;

  // Movement-travel abilities (Charge / Heroic Leap — tag 'movement') are
  // presented by the REMOTE_DASHED path (VFXSystem.onRemoteDashed): trail
  // along the glide + landing at the DESTINATION. Playing the zone/impact
  // here would draw the landing beat at the CAST origin and double-render.
  if (data.tags?.includes('movement')) return plays;

  // ── AoE / ground zones (Blizzard, Meteor Shower, Inferno, Flamestrike, …) ──
  // Mirror AOEBehavior: zone effect = castEffect || 'aoe_blast' at target/caster pos.
  if (target === 'aoe' || target === 'ground') {
    const zoneX = targetX !== undefined ? targetX : casterX;
    const zoneY = targetY !== undefined ? targetY : casterY;
    const zoneEffectId = vfx?.castEffect || REMOTE_VFX_CONFIG.aoeZoneFallbackEffectId;
    plays.push({
      effectId: zoneEffectId,
      x: zoneX,
      y: zoneY,
      category: 'aoe_zone',
      overrides: {
        abilityId,
        duration: REMOTE_VFX_CONFIG.zoneDurationMs,
        radius: data.radius ?? REMOTE_VFX_CONFIG.defaultAoeRadius,
      },
    });

    // The ability's own impact_effect (e.g. frost_storm, fire_explosion,
    // arcane_explosion) — the local side draws this via combat:area_hit at the
    // zone centre. Mirror it at the target position so the remote screen shows
    // the same burst. Skip if it duplicates the zone effect id.
    if (vfx?.impactEffect && vfx.impactEffect !== zoneEffectId) {
      plays.push({
        effectId: vfx.impactEffect,
        x: zoneX,
        y: zoneY,
        category: 'aoe_impact',
        overrides: { abilityId },
      });
    }
    return plays;
  }

  // ── Self / aura buffs visible to others (Mana Shield, Haste, auras, …) ──
  // Mirror withBuff / withHeal: cast aura = castEffect || 'defensive_aura',
  // tinted by auraColor (or school colour). Only abilities that actually carry
  // an auraColor or castEffect get a zone-style aura here; pure utility/self
  // abilities with a projectile/cast-glow mapping are left to the existing
  // cast-glow path to avoid double effects.
  const isBuffLike =
    target === 'self' &&
    (data.abilityType === 'buff' || data.abilityType === 'defensive' || data.abilityType === 'heal');
  if (isBuffLike && (vfx?.auraColor || vfx?.castEffect)) {
    const auraEffectId = vfx?.castEffect || REMOTE_VFX_CONFIG.auraFallbackEffectId;
    plays.push({
      effectId: auraEffectId,
      x: casterX,
      y: casterY,
      category: 'aura',
      overrides: {
        abilityId,
        color: parseColor(vfx?.auraColor) ?? SCHOOL_COLORS[school],
        duration: data.durationMs ?? REMOTE_VFX_CONFIG.zoneDurationMs,
      },
    });
  }

  return plays;
}
